import React, { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  TextField,
  CircularProgress,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useDebounce } from "@/shared/hooks/useDebounce";
import {
  autocompleteAddress,
  getPlaceDetails,
  isLocationSearchEnabled,
  type AddressSuggestion,
  type PlaceDetails,
} from "@/shared/api/location/geoPlacesClient";

export interface AddressAutocompleteProps {
  /** Current street-address value (controlled by the parent form). */
  value: string;
  /** Called as the user types — keeps the parent's address field in sync. */
  onInputChange: (text: string) => void;
  /** Called when a suggestion is selected, with resolved address + coordinates. */
  onSelect: (details: PlaceDetails) => void;
  /** Called when resolving a selected suggestion's details fails. */
  onLookupError?: () => void;
  /** Called when the background suggestion request fails. */
  onSearchError?: () => void;
  /** Reports only selected-place detail resolution, not background searches. */
  onLookupPendingChange?: (pending: boolean) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  sx?: SxProps<Theme>;
}

export const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onInputChange,
  onSelect,
  onLookupError,
  onSearchError,
  onLookupPendingChange,
  disabled,
  autoFocus,
  error,
  helperText,
  required,
  sx,
}) => {
  const [options, setOptions] = useState<AddressSuggestion[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const debouncedValue = useDebounce(value, 300);
  const abortRef = useRef<AbortController | null>(null);
  const detailAbortRef = useRef<AbortController | null>(null);
  // The label we just auto-filled from a selection — don't re-query for it.
  const justSelectedRef = useRef<string | null>(null);
  const searchErrorRef = useRef(onSearchError);

  useEffect(() => {
    searchErrorRef.current = onSearchError;
  }, [onSearchError]);

  useEffect(() => {
    if (!isLocationSearchEnabled() || disabled) {
      abortRef.current?.abort();
      abortRef.current = null;
      setOptions([]);
      setSearchLoading(false);
      return;
    }

    const query = debouncedValue.trim();
    if (query.length < 3 || query === justSelectedRef.current) {
      abortRef.current?.abort();
      abortRef.current = null;
      setOptions([]);
      setSearchLoading(false);
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setSearchLoading(true);

    autocompleteAddress(query, controller.signal)
      .then((results) => {
        if (!controller.signal.aborted) setOptions(results);
      })
      .catch((err) => {
        if (
          abortRef.current === controller &&
          (err as Error).name !== "AbortError"
        ) {
          setOptions([]);
          searchErrorRef.current?.();
        }
      })
      .finally(() => {
        if (abortRef.current === controller) {
          abortRef.current = null;
          setSearchLoading(false);
        }
      });

    return () => {
      controller.abort();
      if (abortRef.current === controller) abortRef.current = null;
    };
  }, [debouncedValue, disabled]);

  useEffect(
    () => () => {
      detailAbortRef.current?.abort();
      detailAbortRef.current = null;
    },
    [],
  );

  useEffect(() => {
    if (!disabled) return;
    detailAbortRef.current?.abort();
    detailAbortRef.current = null;
    setDetailLoading(false);
    onLookupPendingChange?.(false);
  }, [disabled, onLookupPendingChange]);

  const loading = searchLoading || detailLoading;

  return (
    <Autocomplete<AddressSuggestion, false, false, true>
      freeSolo
      disabled={disabled}
      options={options}
      loading={loading}
      filterOptions={(opts) => opts} // server already filtered; don't filter client-side
      // The text field is controlled by `value`; options are objects.
      inputValue={value}
      onInputChange={(_, newInput, reason) => {
        if (reason === "input" || reason === "clear") {
          abortRef.current?.abort();
          abortRef.current = null;
          setSearchLoading(false);
          setOptions([]);
          // A selected place may still be resolving. Once the user types
          // again, that response is stale and must not overwrite newer text
          // (or attach its coordinates to a different address).
          detailAbortRef.current?.abort();
          detailAbortRef.current = null;
          setDetailLoading(false);
          onLookupPendingChange?.(false);
          justSelectedRef.current = null;
          onInputChange(newInput);
        }
      }}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.label
      }
      isOptionEqualToValue={(option, val) => option.placeId === val.placeId}
      onChange={(_, selected) => {
        if (!selected || typeof selected === "string") return;
        abortRef.current?.abort();
        abortRef.current = null;
        setSearchLoading(false);
        detailAbortRef.current?.abort();
        const controller = new AbortController();
        detailAbortRef.current = controller;
        justSelectedRef.current = selected.label;
        setOptions([]);
        setDetailLoading(true);
        onLookupPendingChange?.(true);
        getPlaceDetails(selected.placeId, controller.signal)
          .then((details) => {
            if (!controller.signal.aborted) {
              justSelectedRef.current = details.address;
              onSelect(details);
            }
          })
          .catch((err) => {
            if ((err as Error).name === "AbortError") return;
            // Leave fields as-is; user can correct manually.
            onLookupError?.();
          })
          .finally(() => {
            if (detailAbortRef.current === controller) {
              detailAbortRef.current = null;
              setDetailLoading(false);
              onLookupPendingChange?.(false);
            }
          });
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Address"
          variant="outlined"
          fullWidth
          autoFocus={autoFocus}
          required={required}
          disabled={disabled}
          error={error}
          helperText={helperText}
          sx={sx}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={18} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
